// src/modules/app-views/_apps/appmulti/operators/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import appService from 'services/appService';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import EntityRecordTab from 'modules/app-views/companies/pages/Document/tabs/RecordTab';
import { FileSyncOutlined, ApiOutlined } from '@ant-design/icons';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import EnrichmentTab from 'modules/app-views/companies/pages/Document/tabs/EnrichmentTab';
import OperatorsApiIntegrationTab from './tabs/ApiIntegrationTab';
import operatorsService from 'modules/app-views/_apps/appmulti/operators/service';
import entitiesService from 'services/entitiesService';
import { returnOnlyNumbers } from 'utils/validation';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;
const STYLES = {
  cardHeader: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OperatorsDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Operators are always company type - no hybrid mode
  const entityTypeModel = 'company';
  const isHybridMode = false;
  const allowedEntityTypes = ['company'];
  const entityProfile = ['operator'];

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: 'company',
    entityProfile,
  });

  // Build requiredFields for operators
  const requiredFields = useMemo(() => {
    const mandatoryFields = ['countryCode', 'name'];

    const storedParameters = appService.getModulePreferences({ category: 'requiredFields' });
    const userConfiguredFields = Array.isArray(storedParameters) ? storedParameters : [];

    const fields = [...mandatoryFields];

    // Company-specific fields
    if (userConfiguredFields.includes('cnpj')) fields.push('cnpj.value');
    if (userConfiguredFields.includes('fullName')) fields.push('fullName');
    if (userConfiguredFields.includes('phone')) fields.push('phone.value');
    if (userConfiguredFields.includes('email')) fields.push('email.value');

    return fields;
  }, [storedCurrentPage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  useEffect(() => {
    const childProps = {
      ...props,
      data,
      initTab,
      state,
      setState,
      viewMode,
      entityTypeModel,
      isHybridMode,
      allowedEntityTypes,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const entityId = values?._id || allValues?._id || data?._id;
      const isNewDocument = !_id || _id === 'add';

      // Block save if CNPJ already registered in another entity
      const cnpjToCheck = returnOnlyNumbers(allValues?.cnpj?.value || values?.cnpj?.value);
      if (cnpjToCheck) {
        const existing = await entitiesService.searchByDocument({ key: 'cnpj', value: cnpjToCheck });
        if (existing && String(existing._id) !== String(entityId)) {
          appService.notification({
            key: 'cnpjAlreadyRegisteredKey',
            flag: 'e',
            title: translateX('cnpj_already_registered_title'),
            description: translateX('cnpj_already_registered_description'),
            duration: 8,
          });
          return;
        }
      }

      // === PARAMETERS TAB: save only _integrations and parameters ===
      if (tab === 'parameters') {
        // Validate credentials when API is enabled — required only in this tab
        const apiEnabledInForm = allValues?._integrations?.apiEnabled;
        const login = allValues?._integrations?.apiCredentials?.login;
        const password = allValues?._integrations?.apiCredentials?.password;

        if (apiEnabledInForm && (!login || !password)) {
          const errorFields = [];
          if (!login) errorFields.push({ name: ['_integrations', 'apiCredentials', 'login'], errors: [translateX('required_field')] });
          if (!password) errorFields.push({ name: ['_integrations', 'apiCredentials', 'password'], errors: [translateX('required_field')] });
          form.setFields(errorFields);
          return;
        }

        const cnpjValue =
          allValues?.cnpj?.value ||
          data?.documents?.find(d => d.documentType === 'cnpj')?.value ||
          data?.cnpj?.value ||
          '';
        const detectedProvider = operatorsService.resolveProviderByCnpj(cnpjValue);
        const dataToSave = {};
        if (allValues?._integrations) {
          dataToSave._integrations = {
            ...allValues._integrations,
            ...(detectedProvider?.provider ? { providerCode: detectedProvider.provider } : {}),
          };
        }
        if (allValues?.parameters) {
          dataToSave.parameters = allValues.parameters;
        }
        // Include conditionsFileUrl (PDF upload) — no Form.Item registered,
        // so it's absent from allValues; read directly from form store.
        const conditionsUrl = form.getFieldValue(['parameters', 'conditionsFileUrl']);
        if (conditionsUrl !== undefined) {
          dataToSave.parameters = dataToSave.parameters || {};
          dataToSave.parameters.conditionsFileUrl = conditionsUrl;
        }
        await upsert({ dataSet: dataToSave, _id: entityId });
        return;
      }

      // === RECORD / ENRICHMENT TABS: save form diff, excluding API integration fields ===
      const dataToSave = { ...values };

      // Strip API integration fields — saved exclusively via the parameters tab
      delete dataToSave._integrations;
      delete dataToSave.parameters;

      // For new documents, explicitly set origin to manual
      if (isNewDocument) {
        dataToSave._metadata = { ...dataToSave._metadata, origin: 0 };
      }

      // Include contacts array to prevent losing contacts
      let mergedContacts = data?.contacts ? [...data.contacts] : [];

      const isEmpty = val =>
        val === null ||
        val === undefined ||
        val === '' ||
        (typeof val === 'string' && val.trim() === '');

      // Sync phone changes
      const phoneFromForm = allValues?.phone || values?.phone;
      const phoneId = data?.phone?._id;
      const currentPhoneValue = phoneFromForm?.value;

      if (phoneId) {
        if (isEmpty(currentPhoneValue)) {
          mergedContacts = mergedContacts.filter(c => c._id !== phoneId);
          dataToSave.phone = null;
        } else if (currentPhoneValue !== data?.phone?.value) {
          mergedContacts = mergedContacts.map(c =>
            c._id === phoneId ? { ...c, value: currentPhoneValue } : c
          );
        }
      }

      // Sync email changes
      const emailFromForm = allValues?.email || values?.email;
      const emailId = data?.email?._id;
      const currentEmailValue = emailFromForm?.value;

      if (emailId) {
        if (isEmpty(currentEmailValue)) {
          mergedContacts = mergedContacts.filter(c => c._id !== emailId);
          dataToSave.email = null;
        } else if (currentEmailValue !== data?.email?.value) {
          mergedContacts = mergedContacts.map(c =>
            c._id === emailId ? { ...c, value: currentEmailValue } : c
          );
        }
      }

      if (data?.contacts || mergedContacts.length > 0) {
        dataToSave.contacts = mergedContacts;
      }

      // Handle CNPJ clearing
      const cnpjFromForm = allValues?.cnpj || values?.cnpj;
      const currentCnpjValue = cnpjFromForm?.value;

      if (isEmpty(currentCnpjValue) && data?.cnpj?.value) {
        dataToSave.cnpj = null;
      }

      await upsert({ dataSet: dataToSave, _id: entityId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="cnpj.value"
          additionalValues={{
            entityProfile: 'operator',
          }}
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeader}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OperatorsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data, viewMode } = props;
  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <EntityRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'enrichment',
      icon: <FileSyncOutlined />,
      label: <span>{translateX('additional_info')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <EnrichmentTab />,
    },
    {
      key: 'parameters',
      icon: <ApiOutlined />,
      label: <span>{translateX('api_integration')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <OperatorsApiIntegrationTab />,
    },
  ];
}
